import { Suspense, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BrowserRouter, Outlet, Route, Routes } from "react-router";
import { Bounce, ToastContainer } from "react-toastify";

import { Authenticated, I18nProvider, Refine } from "@refinedev/core";
import { DevtoolsPanel, DevtoolsProvider } from "@refinedev/devtools";
import { RefineKbar, RefineKbarProvider } from "@refinedev/kbar";
import { ErrorComponent, RefineSnackbarProvider, ThemedLayout as ThemedLayoutV2 } from "@refinedev/mui";
import routerProvider from "@refinedev/react-router";

import { Box, Button, CssBaseline, GlobalStyles, Stack, Typography } from "@mui/material";

import { Header } from "@/components";
import { RouteLoadingFallback } from "@/components/common";
import { ConsentOverlay } from "@/components/consent";
import { InactivityTimeoutOverlay } from "@/components/session-timeout";
import { resources } from "@/config";
import { MessengerProvider } from "@/contexts";
import { ColorModeContextProvider } from "@/contexts";
// Import FHIR client for FHIR R4 API communication
import { FhirClient } from "@/fhir";
import {
  LazyAboutPage,
  LazyAnalytics,
  LazyDashboard,
  LazyForgotPassword,
  LazyLogin,
  LazyOrganizationSettings,
  LazyPatientHistory,
  LazyPatientList,
  LazyPractitioner,
  LazyProfile,
  LazyRegister,
  LazySetupMfaPage,
  LazySetupNewPassword,
  LazySubmitResetPassword,
  LazyVerifyMfaPage,
} from "@/routes/lazy-routes";
import { IUser } from "@/types";

import * as Sentry from "@sentry/react";
import { ErrorBoundary } from "@sentry/react";
import { Provider as UrqlProvider } from "urql";

import { createAccessControlProvider } from "./providers/access-control-provider";
import { validateEnvironmentVariables } from "./providers/auth/auth-utils";
import { createFhirAuthProvider, getHandle401Error } from "./providers/auth/fhir-auth-provider";
import { getIdToken } from "./providers/auth/token-provider";
import { createFhirDataProvider } from "./providers/data/fhir-data-provider";
import { notificationProvider } from "./providers/notification-provider";
import { graphqlClient } from "./utils/GraphQLClient";
import { getJwtExpiration } from "./utils/jwt-decode";

// Module-level flag set by Vite's preload error event when a dynamic import chunk fails to load
export let lastPreloadError: unknown = null;

export function _resetPreloadError() {
  lastPreloadError = null;
}

window.addEventListener("vite:preloadError", (event: Event) => {
  lastPreloadError = (event as CustomEvent & { payload?: unknown }).payload ?? event;
});

// Validate environment configuration at startup
if (!validateEnvironmentVariables()) {
  console.warn("Some features may be unavailable until environment variables are configured");
}

// Create FHIR R4 client instance
const fhirClient = new FhirClient({
  baseUrl: import.meta.env.VITE_FHIR_BASE_URL || "http://localhost:8103/fhir/R4",
  tokenProvider: async () => getIdToken(),
  timeout: 30000,
  onError: error => {
    console.error("FHIR Client Error:", error);

    // Handle 401 errors by attempting token refresh or redirecting to login
    if (error.statusCode === 401) {
      getHandle401Error()()
        .then(result => {
          if (result.logout && result.redirectTo) {
            // Redirect to login page
            window.location.href = result.redirectTo;
          }
        })
        .catch(err => {
          // Log unexpected errors in 401 handling to Sentry
          Sentry.captureException(err, {
            level: "error",
            tags: { component: "fhir-client", operation: "handle-401-error" },
            extra: { originalError: error },
          });
          // Fallback: force redirect to login
          window.location.href = "/login";
        });
    }
  },
});

// AppContent component that uses React Router hooks inside BrowserRouter
function AppContent() {
  const { t: translate, i18n } = useTranslation();

  // Create auth provider using FHIR client.
  // Memoized so the same closure instance (and its cachedUserData) is preserved.
  const authProvider = useMemo(
    () =>
      createFhirAuthProvider({
        authBaseUrl: import.meta.env.VITE_AUTH_BASE_URL || "http://localhost:8103/auth",
        loginBaseUrl: import.meta.env.VITE_LOGIN_BASE_URL || "http://localhost:8103/practitioner/auth",
        fhirClient,
        // Transform request to match API contract (email → username)
        transformLoginRequest: (email, password) => ({
          username: email,
          password,
        }),
        /**
         * Transform Cognito login response to AuthTokens format
         *
         * Cognito response: { access, id, valid, challenge }
         * - access: JWT access token (for API auth, contains: sub, cognito:groups, scope)
         * - id: JWT ID token (user claims: email, email_verified, cognito:groups)
         * - valid: boolean indicating successful auth
         * - challenge: MFA challenge object or null
         */
        transformLoginResponse: data => {
          // Extract expiration from JWT token
          const response = data as {
            access: string;
            id: string;
            refresh?: string;
            valid: boolean;
            challenge?: { challengeName: string; session: string; challengeParameters?: Record<string, string> } | null;
          };
          // eslint-disable-next-line react-hooks/purity -- Runs during auth callback, not render
          const expiresAt = getJwtExpiration(response.access) || Date.now() + 3600000; // Default to 1 hour if decode fails

          return {
            accessToken: response.access,
            idToken: response.id,
            refreshToken: response.refresh,
            expiresAt,
            valid: response.valid,
            challenge: response.challenge,
          };
        },
        onTokenChange: token => {
          // Update FHIR client token provider when auth state changes
          if (token) {
            fhirClient.setTokenProvider(async () => token);
          } else {
            fhirClient.clearTokenProvider();
          }
        },
      }),
    []
  );

  // Create data provider using FHIR client
  const dataProvider = createFhirDataProvider(fhirClient);

  // Create access control provider for permission checks
  const accessControlProvider = createAccessControlProvider(
    async () => (await authProvider.getIdentity?.()) as IUser | null
  );

  const i18nProvider: I18nProvider = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Matches Refine's TranslateFunction signature
    translate: (key: string, options?: any, defaultMessage?: string) => {
      if (typeof options === "string") {
        return translate(key, { defaultValue: options }) as string;
      }
      return translate(key, { defaultValue: defaultMessage, ...options }) as string;
    },
    changeLocale: (lang: string) => i18n.changeLanguage(lang),
    getLocale: () => i18n.language,
  };

  return (
    <UrqlProvider value={graphqlClient}>
      <RefineKbarProvider>
        <ColorModeContextProvider>
          <CssBaseline />
          <GlobalStyles styles={{ html: { WebkitFontSmoothing: "auto" } }} />
          <ErrorBoundary
            fallback={() => {
              // lastPreloadError is set by the vite:preloadError event when a chunk fails to load
              if (lastPreloadError) {
                return (
                  <Stack spacing={3} alignItems="center" justifyContent="center" height="100vh">
                    <Typography variant="h5">{translate("errors.newVersionAvailable")}</Typography>
                    <Typography variant="body1">{translate("errors.pleaseReloadPage")}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {translate("errors.ifPersistsContactSupport")}
                    </Typography>
                    <Button variant="contained" onClick={() => window.location.reload()}>
                      {translate("errors.reloadPage")}
                    </Button>
                  </Stack>
                );
              }

              return (
                <Stack spacing={3} alignItems="center" justifyContent="center" height="100vh">
                  <Typography variant="h5">{translate("errors.somethingWentWrong")}</Typography>
                  <Typography variant="body1">{translate("errors.contactSupport")}</Typography>
                </Stack>
              );
            }}
          >
            <RefineSnackbarProvider>
              <DevtoolsProvider>
                <Refine
                  dataProvider={dataProvider}
                  notificationProvider={notificationProvider}
                  i18nProvider={i18nProvider}
                  routerProvider={routerProvider}
                  authProvider={authProvider}
                  accessControlProvider={accessControlProvider}
                  resources={resources}
                  options={{
                    syncWithLocation: true,
                    warnWhenUnsavedChanges: true,
                    projectId: "HUZWI0-BQaJ0j-TwllPv",
                  }}
                >
                  <Routes>
                    <Route
                      element={
                        <Authenticated
                          key="authenticated-routes"
                          redirectOnFail="login"
                          appendCurrentPathToQuery={true}
                        >
                          <ConsentOverlay />
                          <InactivityTimeoutOverlay />
                          <MessengerProvider>
                            <ThemedLayoutV2
                              Sider={() => null} // Disable the sidebar
                              Header={props => <Header {...props} resources={resources} />}
                            >
                              <Box width="100%" style={{ maxWidth: 1440, minWidth: 900 }} mx="auto">
                                <Outlet />
                              </Box>
                            </ThemedLayoutV2>
                          </MessengerProvider>
                        </Authenticated>
                      }
                    >
                      {/* Set dashboard resource as the default landing page */}
                      <Route
                        index
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyDashboard />
                          </Suspense>
                        }
                      />
                      <Route
                        path="patients"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyPatientList />
                          </Suspense>
                        }
                      />
                      <Route
                        path="patients/:id"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyPatientList />
                          </Suspense>
                        }
                      />
                      <Route
                        path="patients/:id/history"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyPatientHistory />
                          </Suspense>
                        }
                      />
                      <Route
                        path="practitioner"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyPractitioner />
                          </Suspense>
                        }
                      />
                      <Route
                        path="analytics"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyAnalytics />
                          </Suspense>
                        }
                      />
                      <Route
                        path="profile"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyProfile />
                          </Suspense>
                        }
                      />
                      <Route
                        path="profile/about"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyAboutPage />
                          </Suspense>
                        }
                      />
                      <Route
                        path="profile/organization-settings"
                        element={
                          <Suspense fallback={<RouteLoadingFallback />}>
                            <LazyOrganizationSettings />
                          </Suspense>
                        }
                      />
                      <Route path="*" element={<ErrorComponent />} />
                    </Route>

                    <Route
                      path="login"
                      element={
                        <Suspense fallback={null}>
                          <LazyLogin />
                        </Suspense>
                      }
                    />
                    <Route
                      path="register"
                      element={
                        <Suspense fallback={null}>
                          <LazyRegister />
                        </Suspense>
                      }
                    />
                    <Route
                      path="forgot-password"
                      element={
                        <Suspense fallback={null}>
                          <LazyForgotPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="submit-reset-password"
                      element={
                        <Suspense fallback={null}>
                          <LazySubmitResetPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="setup-new-password"
                      element={
                        <Suspense fallback={null}>
                          <LazySetupNewPassword />
                        </Suspense>
                      }
                    />
                    <Route
                      path="setup-mfa"
                      element={
                        <Suspense fallback={null}>
                          <LazySetupMfaPage />
                        </Suspense>
                      }
                    />
                    <Route
                      path="verify-mfa"
                      element={
                        <Suspense fallback={null}>
                          <LazyVerifyMfaPage />
                        </Suspense>
                      }
                    />
                  </Routes>
                  <RefineKbar />
                </Refine>
                <DevtoolsPanel />
              </DevtoolsProvider>
            </RefineSnackbarProvider>
          </ErrorBoundary>
        </ColorModeContextProvider>
      </RefineKbarProvider>
    </UrqlProvider>
  );
}

// Main App function
function App() {
  return (
    <BrowserRouter basename={import.meta.env.VITE_BASE_URL || "/"}>
      <AppContent />
      {/* ToastContainer at the highest level to ensure it's always rendered */}
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick={false}
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="dark"
        transition={Bounce}
      />
    </BrowserRouter>
  );
}

export default App;
